/**
 * LocalNewsFeed — live Google News headlines for a location slug.
 * Mounts on any city LocationArticle; auto-hides if no items found.
 * Fetched by /api/news/local/{slug} — server-cached for 1 hour.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Newspaper, ExternalLink, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LocalNewsFeed({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(true);
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    axios.get(`${API}/api/news/local/${slug}`)
      .then((r) => alive && setData(r.data || null))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoad(false));
    return () => { alive = false; };
  }, [slug]);

  const items = data?.items || [];
  const daily = data?.daily_counts || [];

  if (loading) {
    return (
      <section className="mt-10" data-testid="local-news-loading">
        <div className="flex items-center gap-2 mb-3">
          <Newspaper className="w-4 h-4" />
          <h2 className="font-display text-xl">Local news</h2>
        </div>
        <div className="text-sm opacity-70">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading fresh headlines…
        </div>
      </section>
    );
  }

  if (!items?.length) return null;

  const maxDaily = Math.max(1, ...daily);

  return (
    <section className="mt-10" data-testid="local-news">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Newspaper className="w-4 h-4" />
        <h2 className="font-display text-xl">Local news</h2>
        {daily.length > 0 && (
          <span
            className="inline-flex items-end gap-[2px] h-6 ml-1"
            title={`Articles per day (past 7d): ${daily.join(", ")}`}
            data-testid="local-news-sparkline"
          >
            {daily.map((v, i) => (
              <span
                key={i}
                className="rounded-sm"
                style={{
                  width:  "5px",
                  height: `${Math.max(3, Math.round((v / maxDaily) * 22))}px`,
                  background: v === 0 ? "#94A3B8" : "#B91C1C",
                  opacity: v === 0 ? 0.35 : 0.9,
                }}
              />
            ))}
          </span>
        )}
        <span className="text-xs opacity-60">via Google News · refreshed hourly</span>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="rounded-xl border p-3 hover:brightness-95 transition"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            data-testid={`local-news-item-${i}`}
          >
            <a href={it.url} target="_blank" rel="noreferrer" className="block">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium hover:underline break-words">{it.title}</div>
                  <div className="text-xs opacity-70 mt-1 flex items-center gap-2 flex-wrap">
                    {it.source && <span className="font-medium">{it.source}</span>}
                    {it.published && <span>· {new Date(it.published).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
                  </div>
                  {it.snippet && <div className="text-xs opacity-80 mt-1 line-clamp-2">{it.snippet}</div>}
                </div>
                <ExternalLink className="w-3.5 h-3.5 opacity-50 mt-1 shrink-0" />
              </div>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
